import { Children, isValidElement, useCallback, useEffect, useMemo, useRef, type CSSProperties, type ReactNode } from 'react';

// import './float-buttons.css';
import '@eshop/styles/footers.scss'
import type { WhatsAppNumberEntry } from './normalizers.ts';
import { WhatsAppFloatButton } from './WhatsAppFloatButton.tsx';
import { useFloatingArea } from '../../../../contexts/FloatingAreaContext.tsx';

export interface FloatingButtonsPanelProps {
  whatsappNumbers?: WhatsAppNumberEntry[];
  messageText?: string | null;
  helpButton?: ReactNode;
  extraButtons?: ReactNode | ReactNode[];
  onWhatsAppExpansionChange?: (expanded: boolean) => void;
  className?: string;
  style?: CSSProperties;
}

const isRenderable = (node: ReactNode): boolean => {
  if (node === null || node === undefined) {
    return false;
  }
  if (typeof node === 'boolean') {
    return false;
  }
  if (Array.isArray(node)) {
    return node.some(isRenderable);
  }
  if (typeof node === 'string' || typeof node === 'number') {
    return true;
  }
  return isValidElement(node);
};

export const FloatingButtonsPanel = ({
  whatsappNumbers,
  messageText,
  helpButton,
  extraButtons,
  onWhatsAppExpansionChange,
  className,
  style,
}: FloatingButtonsPanelProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const auxButtons = useMemo(() => {
    const nodes: ReactNode[] = [];
    if (helpButton) {
      nodes.push(helpButton);
    }
    if (extraButtons) {
      nodes.push(...Children.toArray(extraButtons));
    }
    return nodes.filter(isRenderable);
  }, [extraButtons, helpButton]);

  const hasWhatsapp = Boolean(whatsappNumbers && whatsappNumbers.length > 0);
  const shouldRender = hasWhatsapp || auxButtons.length > 0;

  // Reportar la altura del panel al contexto compartido para que
  // AssistantWidget se desplace y no se solape con estos botones.
  const { setPanelHeight } = useFloatingArea();

  useEffect(() => {
    const node = containerRef.current;
    if (!node || !shouldRender) {
      setPanelHeight(0);
      return;
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setPanelHeight(entry.contentRect.height);
      }
    });
    observer.observe(node);

    return () => {
      observer.disconnect();
      setPanelHeight(0);
    };
  }, [shouldRender, setPanelHeight]);

  const handleExpansion = useCallback(
    (expanded: boolean) => {
      const root = containerRef.current;
      if (!root) {
        return;
      }
      const wrappers = Array.from(
        root.querySelectorAll<HTMLElement>('[data-float-button="aux"]'),
      );
      wrappers.forEach((wrapper, index) => {
        const offset = expanded ? -96 - index * 12 : 0;
        wrapper.style.transform = `translateX(${offset}px)`;
      });
      onWhatsAppExpansionChange?.(expanded);
    },
    [onWhatsAppExpansionChange],
  );

  if (!shouldRender) {
    return null;
  }

  const wrapperClassName = ['float-buttons-container', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperClassName} style={style} ref={containerRef}>
      <div className="float-button-stack">
        {auxButtons.map((node, index) => (
          <div key={`float-aux-${index}`} className="float-button-wrapper" data-float-button="aux">
            {node}
          </div>
        ))}
        {hasWhatsapp ? (
          <div className="float-button-wrapper" data-float-button="whatsapp">
            <WhatsAppFloatButton
              wspNumbers={whatsappNumbers ?? []}
              floatBtnMsjTxt={messageText}
              onExpansionChange={handleExpansion}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default FloatingButtonsPanel;
